import { useCallback, useEffect, useMemo, useRef, useState, type MouseEvent } from "react";
import { Check, MoreVertical, Search, ShieldCheck, X } from "lucide-react";
import { usePage, type SectionProps } from "./context";
import { Band, Head, cn, rv, str } from "./ui";
import { useAuth } from "./auth";
import { tagsApi, type Tag, type TagGroup } from "./contacts-api";
import { tagColors, tagEmojis } from "./tags-meta";
import { navLabel, settingsNav as tagsNav, settingsNavAnchors as tagsNavAnchors } from "./settings-nav";
import { ApiError, apiErrorText } from "@/lib/api";
import { goAnchor } from "./app-nav";
import { t, tn } from "@/lib/i18n";

/* «Теги»: метки контактов с эмодзи, цветом и описанием — с сервера (/api/tags).
   Группы тегов — как Reporting Tags в Zoho Books: «Department» со списком значений, у контакта по группе одно
   значение (в карточке — выпадающий список). Простые теги — как раньше.
   Создают, меняют, дублируют и удаляют теги владелец, администраторы и менеджеры; агенты только смотрят.
   Удаление тега снимает его со всех контактов, удаление группы удаляет её значения; сами контакты остаются. */

/* kind: "tag" — новый или простой тег (значения вписываются в диалоге; если есть — тег станет выпадающим списком),
   "group" — существующий тег со списком (значения добавляются/удаляются сразу на сервере), "value" — одно значение списка */
type Draft = { kind: "tag" | "group" | "value"; id: string | null; emoji: string; name: string; color: string; description: string; values: string[]; valueInput: string };

const EMPTY_DRAFT: Draft = { kind: "tag", id: null, emoji: "🏷️", name: "", color: "teal", description: "", values: [], valueInput: "" };
const MONTHS = [t("янв"), t("фев"), t("мар"), t("апр"), t("мая"), t("июн"), t("июл"), t("авг"), t("сен"), t("окт"), t("ноя"), t("дек")];
const FIELD =
  "h-10 w-full box-border min-w-0 rounded-btn border border-border bg-background px-3 text-[13px] text-foreground placeholder:text-muted-foreground focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/25";

const colorOf = (id: string) => tagColors.find((c) => c.id === id) ?? tagColors[0];
const tint = (hex: string) => `${hex}22`;

const formatDate = (iso: string) => {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return `${d.getDate()} ${MONTHS[d.getMonth()]} ${d.getFullYear()}`;
};

// name_taken у тегов — своё сообщение (общий текст apiErrorText — про команды)
const errText = (err: unknown) =>
  err instanceof ApiError && err.message === "name_taken" ? t("Такой тег уже есть — выберите другое название") : apiErrorText(err);

function TagsDemo({ title, subtitle }: SectionProps) {
  const { brand } = usePage();
  const { user } = useAuth();
  const canEdit = Boolean(user && ["owner", "admin", "manager"].includes(user.role));
  const [items, setItems] = useState<Tag[]>([]);
  const [groups, setGroups] = useState<TagGroup[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [busy, setBusy] = useState(false);
  const [query, setQuery] = useState("");
  const [menu, setMenu] = useState<{ id: string; top: number; left: number } | null>(null);
  const [draft, setDraft] = useState<Draft | null>(null);
  const [emojiOpen, setEmojiOpen] = useState(false);
  const [confirm, setConfirm] = useState<Tag | TagGroup | null>(null);
  const [toast, setToast] = useState("");
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  useEffect(() => () => clearTimeout(timer.current), []);

  const flash = useCallback((text: string) => {
    setToast(text);
    clearTimeout(timer.current);
    timer.current = setTimeout(() => setToast(""), 4200);
  }, []);

  useEffect(() => {
    Promise.all([tagsApi.list(), tagsApi.groups()])
      .then(([tags, gs]) => {
        setItems(tags);
        setGroups(gs);
      })
      .catch((err) => flash(apiErrorText(err)))
      .finally(() => setLoaded(true));
  }, [flash]);
  const reloadGroups = useCallback(() => tagsApi.groups().then(setGroups, () => undefined), []);

  useEffect(() => {
    const onDown = (e: PointerEvent) => {
      const target = e.target as HTMLElement | null;
      if (target?.closest?.("[data-menu-root]") || target?.closest?.("[data-emoji-root]")) return;
      setMenu(null);
      setEmojiOpen(false);
    };
    document.addEventListener("pointerdown", onDown);
    return () => document.removeEventListener("pointerdown", onDown);
  }, []);

  useEffect(() => {
    if (!menu) return;
    const close = () => setMenu(null);
    window.addEventListener("scroll", close, true);
    return () => window.removeEventListener("scroll", close, true);
  }, [menu]);

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return items;
    return items.filter((t) => `${t.name} ${t.emoji} ${t.description} ${t.group ?? ""}`.toLowerCase().includes(q));
  }, [items, query]);
  // секции: группы (значения — теги группы) и простые теги; при поиске показываем группу, если совпала она сама или её значение
  const sections = useMemo(() => {
    const q = query.trim().toLowerCase();
    const list = groups
      .map((g) => ({ group: g as TagGroup | null, tags: visible.filter((t) => t.groupId === g.id), hit: Boolean(q) && g.name.toLowerCase().includes(q) }))
      .filter((sec) => !q || sec.hit || sec.tags.length > 0);
    const plain = visible.filter((t) => !t.groupId);
    if (plain.length || !q) list.push({ group: null, tags: plain, hit: false });
    return list;
  }, [groups, visible, query]);

  const used = items.reduce((sum, t) => sum + t.count, 0);

  const openAdd = () => {
    setDraft({ ...EMPTY_DRAFT });
    setEmojiOpen(false);
    setMenu(null);
  };

  const openEdit = (t: Tag) => {
    if (!canEdit) return;
    setDraft({ kind: t.groupId ? "value" : "tag", id: t.id, emoji: t.emoji, name: t.name, color: t.color, description: t.description, values: [], valueInput: "" });
    setEmojiOpen(false);
    setMenu(null);
  };

  const openEditGroup = (g: TagGroup) => {
    if (!canEdit) return;
    setDraft({ kind: "group", id: g.id, emoji: g.emoji, name: g.name, color: g.color, description: g.description, values: [], valueInput: "" });
    setEmojiOpen(false);
    setMenu(null);
  };

  // значения существующего списка — теги его группы (живут на сервере)
  const groupValues = draft?.kind === "group" && draft.id ? items.filter((x) => x.groupId === draft.id) : [];

  const addValue = async () => {
    if (!draft) return;
    const v = draft.valueInput.trim().slice(0, 40);
    if (!v) return;
    if (draft.kind === "group" && draft.id) {
      if (groupValues.some((x) => x.name.toLowerCase() === v.toLowerCase())) {
        flash(t("Такое значение в группе уже есть"));
        return;
      }
      try {
        const tag = await tagsApi.create({ name: v, groupId: draft.id });
        setItems((prev) => [...prev, tag]);
        setDraft((d) => d && { ...d, valueInput: "" });
        void reloadGroups();
      } catch (err) {
        flash(errText(err));
      }
      return;
    }
    if (draft.values.some((x) => x.toLowerCase() === v.toLowerCase())) {
      flash(t("Такое значение в группе уже есть"));
      return;
    }
    setDraft((d) => d && { ...d, values: [...d.values, v], valueInput: "" });
  };

  const dropValue = async (tag: Tag) => {
    try {
      await tagsApi.remove(tag.id);
      setItems((prev) => prev.filter((x) => x.id !== tag.id));
      void reloadGroups();
      if (tag.count > 0) flash(t("Значение «{name}» удалено — метка снята с {count}", { name: tag.name, count: tn(tag.count, "контакта", "контактов", "контактов") }));
    } catch (err) {
      flash(errText(err));
    }
  };

  const nameError = (() => {
    if (!draft) return "";
    const name = draft.name.trim().toLowerCase();
    if (!name) return "";
    if (draft.kind === "value") {
      const gid = items.find((x) => x.id === draft.id)?.groupId ?? null;
      if (items.some((t) => t.name.toLowerCase() === name && t.id !== draft.id && t.groupId === gid)) return t("Такое значение в группе уже есть");
      return "";
    }
    // тег со значениями хранится как группа, без значений — как простой тег; названия не должны совпадать ни с теми, ни с другими
    if (groups.some((g) => g.name.toLowerCase() === name && g.id !== draft.id)) return t("Такой тег уже есть — выберите другое название");
    if (items.some((t) => t.name.toLowerCase() === name && t.id !== draft.id && !t.groupId)) return t("Такой тег уже есть — выберите другое название");
    return "";
  })();

  const canSave = Boolean(draft && draft.name.trim().length >= 1 && !nameError && !busy);

  const save = async () => {
    if (!draft || !canSave) return;
    const data = {
      emoji: draft.emoji,
      name: draft.name.trim(),
      color: draft.color,
      description: draft.description.trim(),
    };
    setBusy(true);
    try {
      const pending = draft.valueInput.trim() ? [...draft.values, draft.valueInput.trim().slice(0, 40)] : draft.values;
      if (draft.kind === "group" && draft.id) {
        const g = await tagsApi.updateGroup(draft.id, data);
        setGroups((prev) => prev.map((x) => (x.id === g.id ? g : x)));
        setItems((prev) => prev.map((x) => (x.groupId === g.id ? { ...x, group: g.name } : x)));
        if (draft.valueInput.trim()) await addValue();
        flash(t("Тег «{name}» обновлён — метка на контактах осталась прежней", { name: g.name }));
      } else if (pending.length) {
        // тег со значениями = группа; простой тег, которому дописали значения, превращается в список
        const g = await tagsApi.createGroup({ ...data, values: pending });
        const old = draft.id ? items.find((x) => x.id === draft.id) : null;
        if (old && old.count === 0) await tagsApi.remove(old.id);
        setGroups((prev) => [...prev, g]);
        setItems(await tagsApi.list());
        flash(
          old && old.count > 0
            ? t("Тег «{name}» стал выпадающим списком. Прежний простой тег остался на {count} — снимите его вручную", { name: g.name, count: tn(old.count, "контакте", "контактах", "контактах") })
            : t("Тег «{name}» создан: {n}. В карточке контакта значение выбирается из списка", { name: g.name, n: tn(g.options, "значение", "значения", "значений") }),
        );
      } else if (draft.id) {
        const tag = await tagsApi.update(draft.id, data);
        setItems((prev) => prev.map((x) => (x.id === tag.id ? tag : x)));
        flash(t("Тег «{name}» обновлён — метка на контактах осталась прежней", { name: tag.name }));
      } else {
        const tag = await tagsApi.create(data);
        setItems((prev) => [tag, ...prev]);
        flash(t("Тег «{name}» создан — его можно повесить на контакт", { name: tag.name }));
      }
      setDraft(null);
    } catch (err) {
      flash(errText(err));
    } finally {
      setBusy(false);
    }
  };

  const duplicate = async (tag: Tag) => {
    let n = 2;
    let name = t("{name} (копия)", { name: tag.name });
    while (items.some((x) => x.name.toLowerCase() === name.toLowerCase() && (x.groupId ?? "") === (tag.groupId ?? ""))) {
      n += 1;
      name = t("{name} (копия {n})", { name: tag.name, n });
    }
    setMenu(null);
    try {
      const copy = await tagsApi.create({ name: name.slice(0, 40), emoji: tag.emoji, color: tag.color, description: tag.description, groupId: tag.groupId ?? "" });
      setItems((prev) => {
        const i = prev.findIndex((x) => x.id === tag.id);
        const next = [...prev];
        next.splice(i + 1, 0, copy);
        return next;
      });
      flash(t("Создана копия тега «{name}» — на контактах пока не стоит", { name: tag.name }));
    } catch (err) {
      flash(errText(err));
    }
  };

  const isGroup = (x: Tag | TagGroup): x is TagGroup => "options" in x;

  const remove = async (tag: Tag | TagGroup) => {
    setBusy(true);
    try {
      if (isGroup(tag)) {
        await tagsApi.removeGroup(tag.id);
        setGroups((prev) => prev.filter((x) => x.id !== tag.id));
        setItems((prev) => prev.filter((x) => x.groupId !== tag.id));
        setConfirm(null);
        flash(t("Тег «{name}» удалён вместе со значениями — метки сняты, сами контакты на месте", { name: tag.name }));
        return;
      }
      await tagsApi.remove(tag.id);
      setItems((prev) => prev.filter((x) => x.id !== tag.id));
      setConfirm(null);
      void reloadGroups();
      flash(
        tag.count > 0
          ? t("Тег «{name}» удалён — метка снята с {count}, сами контакты на месте", { name: tag.name, count: tn(tag.count, "контакта", "контактов", "контактов") })
          : t("Тег «{name}» удалён", { name: tag.name }),
      );
    } catch (err) {
      flash(apiErrorText(err));
    } finally {
      setBusy(false);
    }
  };

  const openRowMenu = (e: MouseEvent<HTMLButtonElement>, t: Tag | TagGroup) => {
    const r = e.currentTarget.getBoundingClientRect();
    const width = 200;
    setEmojiOpen(false);
    setMenu({
      id: t.id,
      top: Math.min(r.bottom + 4, window.innerHeight - 200),
      left: Math.max(8, Math.min(window.innerWidth - width - 8, r.right - width)),
    });
  };

  const onNav = (id: string) => {
    if (id === "tags") return;
    const anchor = tagsNavAnchors[id];
    if (anchor) {
      goAnchor(anchor);
      return;
    }
    flash(t("Раздел «{name}» пока не подключён", { name: navLabel(id) }));
  };

  const th = "py-2.5 pe-3 text-start font-normal align-middle last:pe-0";
  const rowPad = "py-2.5";
  const menuTag = menu ? items.find((t) => t.id === menu.id) : null;
  const menuGroup = menu ? groups.find((g) => g.id === menu.id) : null;
  const draftColor = draft ? colorOf(draft.color) : tagColors[0];

  return (
    <Band id="tags" tone="default">
      <Head title={title} subtitle={subtitle} />
      <div {...rv()} className="flash-card relative overflow-hidden rounded-card border border-border bg-card !p-0">
        {/* полоска «окна приложения» */}
        <div className="flex flex-wrap items-center gap-2.5 border-b border-border px-4 py-3">
          <span className="flex gap-1.5" aria-hidden>
            <span className="size-2.5 rounded-full bg-foreground/20" />
            <span className="size-2.5 rounded-full bg-foreground/20" />
            <span className="size-2.5 rounded-full bg-foreground/20" />
          </span>
          <span className="flash-mono ms-1 truncate text-[11px] text-muted-foreground">
            {str(brand) || "Hub"} / {t("настройки → входящие → теги")}
          </span>
          <span className="ms-auto inline-flex items-center gap-2 whitespace-nowrap text-[11px] text-muted-foreground">
            <span className="size-1.5 rounded-full bg-primary" aria-hidden />
            {loaded ? t("Тегов: {n}", { n: items.length }) : t("Загружаем теги…")}
          </span>
        </div>

        <div className="grid lg:grid-cols-[14.5rem_minmax(0,1fr)]">
          {/* ── настройки ── */}
          <div className="hidden flex-col gap-4 border-border p-3 lg:flex lg:border-e">
            {tagsNav.map((g) => (
              <div key={g.group} className="flex flex-col gap-0.5">
                <p className="px-2 pb-1.5 text-[10px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">{g.group}</p>
                {g.items.map((it) => {
                  const active = it.id === "tags";
                  return (
                    <button
                      key={it.id}
                      type="button"
                      onClick={() => onNav(it.id)}
                      aria-current={active ? "page" : undefined}
                      className={cn(
                        "flex cursor-pointer items-center gap-2.5 rounded-btn px-2.5 py-2 text-start text-[12.5px] font-medium transition",
                        active ? "bg-primary/15 text-primary" : "text-muted-foreground hover:bg-muted hover:text-foreground",
                      )}
                    >
                      <i className={cn(it.icon, "text-base leading-none")} aria-hidden />
                      <span className="min-w-0 flex-1 truncate">{it.label}</span>
                    </button>
                  );
                })}
              </div>
            ))}

            <div className="mt-auto rounded-btn border border-border bg-background/60 p-3">
              <p className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-primary">
                <ShieldCheck className="size-3.5" aria-hidden />
                {t("Доступы")}
              </p>
              <p className="mt-1.5 text-[11px] leading-relaxed text-muted-foreground">
                {t("Теги создают и меняют владелец, администраторы и менеджеры. Агенты вешают их на контакты, но не меняют список.")}
              </p>
            </div>
          </div>

          {/* ── список тегов ── */}
          <div className="min-w-0 p-3.5 md:p-5">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div className="min-w-0">
                <h3 className="text-base font-semibold leading-snug">{t("Теги")}</h3>
                <p className="mt-1 max-w-xl text-[12px] leading-relaxed text-muted-foreground">
                  {t("Создавайте и управляйте тегами, чтобы раскладывать контакты по темам: оплата, доставка, возврат.")}
                </p>
              </div>
              <button
                type="button"
                onClick={() => flash(t("Тег вешается на контакт в его карточке или массово из списка контактов. По тегу работает фильтр в контактах."))}
                className="inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap text-[12px] font-semibold text-primary transition hover:opacity-80"
              >
                <i className="ri-book-open-line text-sm leading-none" aria-hidden />
                {t("Узнать больше")}
              </button>
            </div>

            <div className="mt-3 flex gap-1.5 overflow-x-auto pb-1 lg:hidden">
              {tagsNav.flatMap((g) => g.items).map((it) => (
                <button
                  key={it.id}
                  type="button"
                  onClick={() => onNav(it.id)}
                  className={cn(
                    "inline-flex flex-none cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-full border px-2.5 py-1 text-[11px] font-semibold transition",
                    it.id === "tags" ? "border-primary bg-primary text-primary-foreground" : "border-border text-muted-foreground hover:text-foreground",
                  )}
                >
                  <i className={it.icon} aria-hidden />
                  {it.label}
                </button>
              ))}
            </div>

            <div className="mt-4 flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center">
              {canEdit ? (
                <button
                  type="button"
                  onClick={() => openAdd()}
                  className="inline-flex h-9 cursor-pointer items-center justify-center gap-1.5 whitespace-nowrap rounded-btn bg-primary px-3.5 text-[12px] font-semibold text-primary-foreground transition hover:brightness-110"
                >
                  <i className="ri-add-line" aria-hidden />
                  {t("Создать тег")}
                </button>
              ) : null}
              <label className="relative block min-w-0 sm:ms-auto sm:w-64">
                <span className="sr-only">{t("Поиск по тегам")}</span>
                <Search className="pointer-events-none absolute start-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" aria-hidden />
                <input
                  type="search"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder={t("Найти тег: оплата, возврат…")}
                  className="h-9 w-full box-border rounded-btn border border-border bg-background ps-9 pe-3 text-[13px] text-foreground placeholder:text-muted-foreground focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/25"
                />
              </label>
            </div>

            <div className="mt-3 overflow-x-auto">
              <table className="w-full min-w-[620px] border-collapse text-start">
                <thead>
                  <tr className="border-b border-border">
                    <th scope="col" className={th}>
                      <span className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">{t("Тег")}</span>
                    </th>
                    <th scope="col" className={cn(th, "hidden lg:table-cell")}>
                      <span className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">{t("Описание")}</span>
                    </th>
                    <th scope="col" className={th}>
                      <span className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">{t("Контактов")}</span>
                    </th>
                    <th scope="col" className={cn(th, "hidden md:table-cell")}>
                      <span className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">{t("Создан")}</span>
                    </th>
                    <th scope="col" className="w-10 py-2.5 text-end">
                      <span className="sr-only">{t("Действия")}</span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {sections.map((sec) => {
                    const g = sec.group;
                    const gc = g ? colorOf(g.color) : null;
                    return [
                      <tr key={g ? `g-${g.id}` : "plain"} className="border-b border-border/60 bg-muted/30">
                        <td className="py-2 pe-3">
                          <div className="flex items-center gap-2.5">
                            {g && gc ? (
                              <span className="grid size-8 flex-none place-items-center rounded-btn text-base leading-none" style={{ backgroundColor: tint(gc.hex), color: gc.hex }} aria-hidden>
                                {g.emoji}
                              </span>
                            ) : (
                              <span className="grid size-8 flex-none place-items-center rounded-btn bg-muted text-base leading-none text-muted-foreground" aria-hidden>
                                <i className="ri-price-tag-3-line" />
                              </span>
                            )}
                            <div className="min-w-0">
                              {g && canEdit ? (
                                <button type="button" onClick={() => openEditGroup(g)} className="cursor-pointer text-start text-[13px] font-semibold leading-snug hover:text-primary">
                                  {g.name}
                                </button>
                              ) : (
                                <span className="text-[13px] font-semibold leading-snug">{g ? g.name : t("Простые теги")}</span>
                              )}
                              <p className="text-[10.5px] text-muted-foreground">
                                {g ? t("выпадающий список · {n} · в карточке выбирается одно", { n: tn(sec.tags.length, "значение", "значения", "значений") }) : t("ставятся любые, сколько угодно")}
                              </p>
                            </div>
                          </div>
                        </td>
                        <td className="hidden py-2 pe-4 lg:table-cell">
                          <span className="line-clamp-1 max-w-[24rem] text-[12px] text-muted-foreground">{g?.description || "—"}</span>
                        </td>
                        <td className="py-2 pe-3">
                          {g ? <span className="text-[11px] tabular-nums text-muted-foreground">{g.count}</span> : null}
                        </td>
                        <td className="hidden py-2 pe-3 text-[12px] tabular-nums text-muted-foreground md:table-cell">{g ? formatDate(g.createdAt) : ""}</td>
                        <td className="py-2 text-end">
                          {g && canEdit ? (
                            <div className="relative inline-block" data-menu-root>
                              <button
                                type="button"
                                onClick={(e) => (menu?.id === g.id ? setMenu(null) : openRowMenu(e, g))}
                                aria-label={t("Действия с группой {name}", { name: g.name })}
                                aria-expanded={menu?.id === g.id}
                                className="grid size-7 cursor-pointer place-items-center rounded-btn text-muted-foreground transition hover:bg-muted hover:text-foreground"
                              >
                                <MoreVertical className="size-4" aria-hidden />
                              </button>
                            </div>
                          ) : null}
                        </td>
                      </tr>,
                      ...sec.tags.map((tag) => {
                        const c = colorOf(tag.color);
                        return (
                          <tr key={tag.id} className="border-b border-border/60 transition last:border-0 hover:bg-muted/40">
                            <td className={cn(rowPad, "pe-3", g && "ps-6")}>
                              <div className="flex items-center gap-2.5">
                                <span
                                  className="grid size-8 flex-none place-items-center rounded-btn text-base leading-none"
                                  style={{ backgroundColor: tint(c.hex), color: c.hex }}
                                  aria-hidden
                                >
                                  {tag.emoji}
                                </span>
                                {canEdit ? (
                                  <button type="button" onClick={() => openEdit(tag)} className="cursor-pointer text-start text-[13px] font-semibold leading-snug hover:text-primary">
                                    {tag.name}
                                  </button>
                                ) : (
                                  <span className="text-[13px] font-semibold leading-snug">{tag.name}</span>
                                )}
                              </div>
                              <p className="mt-1 line-clamp-1 max-w-[16rem] ps-[42px] text-[11px] text-muted-foreground lg:hidden">{tag.description || t("Без описания")}</p>
                            </td>
                            <td className={cn(rowPad, "hidden pe-4 lg:table-cell")}>
                              <span className="line-clamp-1 max-w-[24rem] text-[12px] text-muted-foreground">{tag.description || "—"}</span>
                            </td>
                            <td className={cn(rowPad, "pe-3")}>
                              <span
                                className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-full border px-2 py-0.5 text-[11px] font-medium tabular-nums"
                                style={{ borderColor: tint(c.hex), color: c.hex }}
                              >
                                <i className="ri-user-line text-[11px] leading-none" aria-hidden />
                                {tag.count}
                              </span>
                            </td>
                            <td className={cn(rowPad, "hidden whitespace-nowrap pe-3 text-[12px] tabular-nums text-muted-foreground md:table-cell")}>{formatDate(tag.createdAt)}</td>
                            <td className={cn(rowPad, "text-end")}>
                              {canEdit ? (
                                <div className="relative inline-block" data-menu-root>
                                  <button
                                    type="button"
                                    onClick={(e) => (menu?.id === tag.id ? setMenu(null) : openRowMenu(e, tag))}
                                    aria-label={t("Действия с тегом {name}", { name: tag.name })}
                                    aria-expanded={menu?.id === tag.id}
                                    className="grid size-7 cursor-pointer place-items-center rounded-btn text-muted-foreground transition hover:bg-muted hover:text-foreground"
                                  >
                                    <MoreVertical className="size-4" aria-hidden />
                                  </button>
                                </div>
                              ) : null}
                            </td>
                          </tr>
                        );
                      }),
                      g && canEdit && !query ? (
                        <tr key={`add-${g.id}`} className="border-b border-border/60">
                          <td colSpan={5} className="py-1.5 ps-6">
                            <button type="button" onClick={() => openEditGroup(g)} className="inline-flex cursor-pointer items-center gap-1 text-[11.5px] font-semibold text-primary hover:underline">
                              <i className="ri-add-line" aria-hidden />
                              {t("Добавить значение")}
                            </button>
                          </td>
                        </tr>
                      ) : null,
                    ];
                  })}
                </tbody>
              </table>
              {loaded && visible.length === 0 && groups.length === 0 ? (
                <div className="py-12 text-center">
                  <span className="mx-auto grid size-12 place-items-center rounded-btn bg-primary/10 text-2xl leading-none text-primary" aria-hidden>
                    <i className="ri-price-tag-3-line" />
                  </span>
                  <p className="mt-3 text-sm font-semibold">{items.length ? t("Тегов не найдено") : t("Тегов пока нет")}</p>
                  <p className="mx-auto mt-1 max-w-sm text-[12px] leading-relaxed text-muted-foreground">
                    {canEdit
                      ? t("Измените запрос или создайте новый тег — с ним контакты проще фильтровать и передавать коллегам.")
                      : t("Измените запрос. Новые теги заводят владелец, администраторы и менеджеры.")}
                  </p>
                  {canEdit ? (
                    <button
                      type="button"
                      onClick={() => openAdd()}
                      className="mt-3 inline-flex h-9 cursor-pointer items-center justify-center gap-1.5 whitespace-nowrap rounded-btn bg-primary px-3.5 text-[12px] font-semibold text-primary-foreground transition hover:brightness-110"
                    >
                      <i className="ri-add-line" aria-hidden />
                      {t("Создать тег")}
                    </button>
                  ) : null}
                </div>
              ) : null}
            </div>

            <div className="mt-3 flex flex-wrap items-center justify-between gap-2 border-t border-border/60 pt-3 text-[11px] text-muted-foreground">
              <span>
                {t("{tags} · на контактах {used}", { tags: tn(visible.length, "тег", "тега", "тегов"), used })}
                {groups.length ? ` · ${tn(groups.length, "группа", "группы", "групп")}` : ""}
              </span>
              <span className="inline-flex items-center gap-1.5">
                <i className="ri-filter-3-line text-[13px] leading-none text-primary" aria-hidden />
                {t("Фильтр в контактах: тег")}
              </span>
            </div>

            <p className="mt-3 text-[11px] text-muted-foreground">
              {canEdit
                ? t("Изменения сохраняются сразу. Удаление тега снимает его со всех контактов.")
                : t("У вас только просмотр: теги меняют владелец, администраторы и менеджеры.")}
            </p>
          </div>
        </div>

        {toast ? (
          <div
            role="status"
            className="pointer-events-none absolute inset-x-3 bottom-3 z-40 mx-auto flex max-w-md items-start gap-2 rounded-btn border border-primary/40 bg-background/95 px-3.5 py-2.5 text-[12px] text-foreground shadow-lift backdrop-blur"
          >
            <Check className="mt-0.5 size-3.5 flex-none text-primary" strokeWidth={3} aria-hidden />
            {toast}
          </div>
        ) : null}

        {/* ── меню действий над группой ── */}
        {menu && menuGroup ? (
          <div role="menu" data-menu-root style={{ top: menu.top, left: menu.left }} className="fixed z-50 w-[200px] overflow-hidden rounded-btn border border-border bg-card p-1 shadow-lift">
            <button type="button" role="menuitem" onClick={() => openEditGroup(menuGroup)} className="flex w-full cursor-pointer items-center gap-2 rounded-btn px-2.5 py-2 text-start text-[12px] font-medium transition hover:bg-muted">
              <i className="ri-pencil-line text-[14px] leading-none text-muted-foreground" aria-hidden />
              {t("Изменить тег и значения")}
            </button>
            <button
              type="button"
              role="menuitem"
              onClick={() => {
                setMenu(null);
                setConfirm(menuGroup);
              }}
              className="flex w-full cursor-pointer items-center gap-2 rounded-btn px-2.5 py-2 text-start text-[12px] font-medium text-red-500 transition hover:bg-red-500/10"
            >
              <i className="ri-delete-bin-line text-[14px] leading-none" aria-hidden />
              {t("Удалить тег со значениями")}
            </button>
          </div>
        ) : null}

        {/* ── меню действий над тегом ── */}
        {menu && menuTag ? (
          <div
            role="menu"
            data-menu-root
            style={{ top: menu.top, left: menu.left }}
            className="fixed z-50 w-[200px] overflow-hidden rounded-btn border border-border bg-card p-1 shadow-lift"
          >
            <button
              type="button"
              role="menuitem"
              onClick={() => openEdit(menuTag)}
              className="flex w-full cursor-pointer items-center gap-2 rounded-btn px-2.5 py-2 text-start text-[12px] font-medium transition hover:bg-muted"
            >
              <i className="ri-pencil-line text-[14px] leading-none text-muted-foreground" aria-hidden />
              {t("Изменить тег")}
            </button>
            <button
              type="button"
              role="menuitem"
              onClick={() => void duplicate(menuTag)}
              className="flex w-full cursor-pointer items-center gap-2 rounded-btn px-2.5 py-2 text-start text-[12px] font-medium transition hover:bg-muted"
            >
              <i className="ri-file-copy-2-line text-[14px] leading-none text-muted-foreground" aria-hidden />
              {t("Дублировать")}
            </button>
            <button
              type="button"
              role="menuitem"
              onClick={() => {
                setMenu(null);
                setConfirm(menuTag);
              }}
              className="flex w-full cursor-pointer items-center gap-2 rounded-btn px-2.5 py-2 text-start text-[12px] font-medium text-red-500 transition hover:bg-red-500/10"
            >
              <i className="ri-delete-bin-line text-[14px] leading-none" aria-hidden />
              {t("Удалить")}
            </button>
          </div>
        ) : null}

        {/* ── окно создания / правки тега ── */}
        {draft ? (
          <div className="absolute inset-0 z-[60] flex items-end justify-center bg-black/65 p-3 backdrop-blur-sm sm:items-center md:p-6">
            <form
              onSubmit={(e) => {
                e.preventDefault();
                void save();
              }}
              role="dialog"
              aria-modal="true"
              aria-labelledby="tag-dialog-title"
              className="relative max-h-full w-full max-w-lg overflow-y-auto rounded-card border border-border bg-card p-5 shadow-lift md:p-6"
            >
              <button
                type="button"
                onClick={() => setDraft(null)}
                aria-label={t("Закрыть окно тега")}
                className="absolute end-3 top-3 grid size-8 cursor-pointer place-items-center rounded-btn text-muted-foreground transition hover:bg-muted hover:text-foreground"
              >
                <X className="size-4" aria-hidden />
              </button>

              <div className="flex items-start gap-3 pe-8">
                <span
                  className="grid size-11 flex-none place-items-center rounded-btn text-2xl leading-none"
                  style={{ backgroundColor: tint(draftColor.hex), color: draftColor.hex }}
                  aria-hidden
                >
                  {draft.emoji}
                </span>
                <div className="min-w-0">
                  <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">
{draft.kind === "value" ? t("Значение списка") : draft.id ? t("Настройка тега") : t("Новый тег")}
                  </p>
                  <h3 id="tag-dialog-title" className="mt-1 text-lg font-semibold leading-snug">
                    {draft.id ? t("Тег «{name}»", { name: draft.name || t("без названия") }) : t("Создать тег")}
                  </h3>
                </div>
              </div>

              <p className="mt-3 text-[11.5px] leading-relaxed text-muted-foreground">
                {draft.kind === "value"
                  ? t("Одно из значений выпадающего списка. Название, эмодзи и цвет — только у этого значения.")
                  : t("Тег без значений ставится на контакт как метка. Добавьте значения — и тег станет выпадающим списком, как Reporting Tag в Zoho Books: в карточке выбирается одно из них.")}
              </p>

              <div className="mt-4 flex flex-col gap-3">
                <div className="flex gap-2">
                  <div className="relative flex flex-col gap-1.5" data-emoji-root>
                    <span className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">{t("Эмодзи")}</span>
                    <button
                      type="button"
                      onClick={() => setEmojiOpen((v) => !v)}
                      aria-expanded={emojiOpen}
                      aria-label={t("Выбрать эмодзи для тега")}
                      className="grid size-10 cursor-pointer place-items-center rounded-btn border border-border bg-background text-lg leading-none transition hover:border-primary/60"
                    >
                      {draft.emoji}
                    </button>
                    {emojiOpen ? (
                      <div className="absolute start-0 top-full z-[70] mt-1 w-[15.5rem] rounded-btn border border-border bg-card p-2 shadow-lift">
                        <p className="px-1 pb-1.5 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">{t("Эмодзи тега")}</p>
                        <div className="grid grid-cols-8 gap-1">
                          {tagEmojis.map((e) => (
                            <button
                              key={e}
                              type="button"
                              onClick={() => {
                                setDraft((d) => d && { ...d, emoji: e });
                                setEmojiOpen(false);
                              }}
                              aria-label={t("Эмодзи {e}", { e })}
                              className={cn(
                                "grid size-7 cursor-pointer place-items-center rounded-btn text-base leading-none transition hover:bg-muted",
                                draft.emoji === e && "bg-primary/15 ring-1 ring-primary/40",
                              )}
                            >
                              {e}
                            </button>
                          ))}
                        </div>
                      </div>
                    ) : null}
                  </div>

                  <label className="flex min-w-0 flex-1 flex-col gap-1.5">
                    <span className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">{t("Название")}</span>
                    <input
                      value={draft.name}
                      onChange={(e) => setDraft((d) => d && { ...d, name: e.target.value })}
                      placeholder={draft.kind === "value" ? t("Значение") : t("Название тега: Отдел, Источник лида, VIP")}
                      autoComplete="off"
                      maxLength={40}
                      className={cn(FIELD, nameError && "border-red-400 focus:border-red-400 focus:ring-red-400/25")}
                    />
                  </label>
                </div>

                {draft.kind !== "value" ? (
                  <div className="flex min-w-0 flex-col gap-1.5">
                    <span className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">{t("Значения выпадающего списка")}</span>
                    {(draft.kind === "group" ? groupValues.map((x) => ({ key: x.id, name: x.name, tag: x })) : draft.values.map((v) => ({ key: v, name: v, tag: null }))).length ? (
                      <ul className="flex flex-wrap gap-1.5">
                        {(draft.kind === "group" ? groupValues.map((x) => ({ key: x.id, name: x.name, tag: x as Tag | null })) : draft.values.map((v) => ({ key: v, name: v, tag: null as Tag | null }))).map((v) => (
                          <li key={v.key} className="inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-[12px] font-medium" style={{ backgroundColor: tint(draftColor.hex), borderColor: tint(draftColor.hex), color: draftColor.hex }}>
                            {v.name}
                            {v.tag && v.tag.count > 0 ? <span className="opacity-70">· {v.tag.count}</span> : null}
                            <button
                              type="button"
                              onClick={() => (v.tag ? void dropValue(v.tag) : setDraft((d) => d && { ...d, values: d.values.filter((x) => x !== v.name) }))}
                              aria-label={t("Удалить значение «{name}»", { name: v.name })}
                              className="-me-1 grid size-4 cursor-pointer place-items-center rounded-full opacity-70 transition hover:opacity-100"
                            >
                              <X className="size-3" strokeWidth={3} aria-hidden />
                            </button>
                          </li>
                        ))}
                      </ul>
                    ) : null}
                    <div className="flex gap-2">
                      <input
                        value={draft.valueInput}
                        onChange={(e) => setDraft((d) => d && { ...d, valueInput: e.target.value })}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            e.preventDefault();
                            void addValue();
                          }
                        }}
                        placeholder={t("Новое значение: Furniture, Household…")}
                        autoComplete="off"
                        maxLength={40}
                        className={FIELD}
                      />
                      <button
                        type="button"
                        onClick={() => void addValue()}
                        disabled={!draft.valueInput.trim()}
                        className="inline-flex h-10 flex-none cursor-pointer items-center gap-1 whitespace-nowrap rounded-btn border border-border bg-background px-3 text-[12.5px] font-semibold transition hover:bg-muted disabled:cursor-not-allowed disabled:opacity-50"
                      >
                        <i className="ri-add-line" aria-hidden />
                        {t("Добавить")}
                      </button>
                    </div>
                    <p className="text-[11px] text-muted-foreground">
                      {draft.kind === "group"
                        ? t("Значения сохраняются сразу. Удаление значения снимает его с контактов.")
                        : t("Enter или «Добавить» — ещё одно значение. Без значений тег останется простой меткой.")}
                    </p>
                  </div>
                ) : null}

                <div className="flex min-w-0 flex-col gap-1.5">
                  <span className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">{t("Цвета")}</span>
                  <div className="flex flex-wrap items-center gap-2">
                    {tagColors.map((c) => {
                      const on = draft.color === c.id;
                      return (
                        <button
                          key={c.id}
                          type="button"
                          onClick={() => setDraft((d) => d && { ...d, color: c.id })}
                          aria-label={t("Цвет: {color}", { color: c.label })}
                          aria-pressed={on}
                          title={c.label}
                          className={cn(
                            "grid size-7 cursor-pointer place-items-center rounded-full transition",
                            on ? "ring-2 ring-foreground/70 ring-offset-2 ring-offset-card" : "hover:scale-110",
                          )}
                          style={{ backgroundColor: c.hex }}
                        >
                          {on ? <Check className="size-3.5 text-white" strokeWidth={3} aria-hidden /> : null}
                        </button>
                      );
                    })}
                  </div>
                </div>

                <label className="flex min-w-0 flex-col gap-1.5">
                  <span className="flex items-center justify-between gap-2 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                    <span>{t("Описание")}</span>
                    <span className="normal-case tracking-normal tabular-nums">{draft.description.length}/200</span>
                  </span>
                  <textarea
                    value={draft.description}
                    onChange={(e) => setDraft((d) => d && { ...d, description: e.target.value.slice(0, 200) })}
                    rows={3}
                    placeholder={t("Опишите тег")}
                    className={cn(FIELD, "h-auto min-h-20 resize-y py-2.5 leading-relaxed")}
                  />
                </label>
              </div>

              {nameError ? <p className="mt-2 text-[11px] text-red-500">{nameError}</p> : null}
              {!canSave && !nameError ? (
                <p className="mt-2 text-[11px] text-muted-foreground">{t("Придумайте название — по нему тег ищут в фильтрах.")}</p>
              ) : null}

              <p className="mt-3 flex items-start gap-2 rounded-btn border border-border bg-background/60 px-3 py-2.5 text-[11px] leading-relaxed text-muted-foreground">
                <ShieldCheck className="mt-0.5 size-3.5 flex-none text-primary" aria-hidden />
                {t("Тег виден всем сотрудникам рабочего пространства. Удаление тега не удаляет контакты — метка просто снимается.")}
              </p>

              <div className="mt-4 flex flex-col-reverse items-stretch gap-2 sm:flex-row sm:items-center sm:justify-end">
                <button
                  type="button"
                  onClick={() => setDraft(null)}
                  className="inline-flex h-10 cursor-pointer items-center justify-center whitespace-nowrap rounded-btn border border-border px-4 text-[13px] font-semibold text-muted-foreground transition hover:text-foreground"
                >
                  {t("Отмена")}
                </button>
                <button
                  type="submit"
                  disabled={!canSave}
                  className="inline-flex h-10 cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-btn bg-primary px-4 text-[13px] font-semibold text-primary-foreground transition hover:brightness-110 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <Check className="size-4" aria-hidden />
                  {busy ? t("Сохраняем…") : draft.id ? t("Сохранить") : t("Создать тег")}
                </button>
              </div>
            </form>
          </div>
        ) : null}

        {/* ── подтверждение удаления ── */}
        {confirm ? (
          <div className="absolute inset-0 z-[60] flex items-end justify-center bg-black/65 p-3 backdrop-blur-sm sm:items-center md:p-6">
            <div
              role="dialog"
              aria-modal="true"
              aria-labelledby="tag-confirm-title"
              className="w-full max-w-sm rounded-card border border-border bg-card p-5 shadow-lift"
            >
              <div className="flex items-start gap-3">
                <span className="grid size-10 flex-none place-items-center rounded-btn bg-red-500/10 text-red-500">
                  <i className="ri-delete-bin-line text-lg leading-none" aria-hidden />
                </span>
                <div className="min-w-0">
                  <h3 id="tag-confirm-title" className="text-base font-semibold leading-snug">
                    {t("Удалить тег «{name}»?", { name: confirm.name })}
                  </h3>
                  <p className="mt-1.5 text-[12px] leading-relaxed text-muted-foreground">
                    {isGroup(confirm)
                      ? t("Удалятся все {n} списка, метки снимутся с {count}. Сами контакты и переписка останутся.", {
                          n: tn(confirm.options, "значение", "значения", "значений"),
                          count: tn(confirm.count, "контакта", "контактов", "контактов"),
                        })
                      : confirm.count > 0
                      ? t("Метка снимется с {count}. Сами контакты и переписка останутся.", { count: tn(confirm.count, "контакта", "контактов", "контактов") })
                      : t("Тег пока не стоит ни на одном контакте, ничего не потеряется.")}
                  </p>
                </div>
              </div>
              <div className="mt-4 flex flex-col-reverse items-stretch gap-2 sm:flex-row sm:justify-end">
                <button
                  type="button"
                  onClick={() => setConfirm(null)}
                  className="inline-flex h-9 cursor-pointer items-center justify-center whitespace-nowrap rounded-btn border border-border px-3.5 text-[12px] font-semibold text-muted-foreground transition hover:text-foreground"
                >
                  {t("Отмена")}
                </button>
                <button
                  type="button"
                  onClick={() => void remove(confirm)}
                  disabled={busy}
                  className="inline-flex h-9 cursor-pointer items-center justify-center whitespace-nowrap rounded-btn bg-red-500 px-3.5 text-[12px] font-semibold text-white transition hover:brightness-110"
                >
                  {t("Удалить тег")}
                </button>
              </div>
            </div>
          </div>
        ) : null}
      </div>
    </Band>
  );
}

export default TagsDemo;